import { createContext, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';

const STORAGE_KEY = 'bsides-app-sidebar-collapsed';

export interface LayoutContextValue {
  appSidebarCollapsed: boolean;
  setAppSidebarCollapsed: (value: boolean) => void;
  toggleAppSidebar: () => void;
}

export const LayoutContext = createContext<LayoutContextValue | null>(null);

function readInitial(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

export function LayoutProvider({ children }: { children: ReactNode }) {
  const [appSidebarCollapsed, setCollapsedState] = useState<boolean>(readInitial);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, appSidebarCollapsed ? '1' : '0');
    } catch {
      // ignore quota / private-mode errors
    }
  }, [appSidebarCollapsed]);

  const setAppSidebarCollapsed = useCallback((value: boolean) => {
    setCollapsedState(value);
  }, []);

  const toggleAppSidebar = useCallback(() => {
    setCollapsedState((v) => !v);
  }, []);

  const value = useMemo<LayoutContextValue>(
    () => ({ appSidebarCollapsed, setAppSidebarCollapsed, toggleAppSidebar }),
    [appSidebarCollapsed, setAppSidebarCollapsed, toggleAppSidebar],
  );

  return <LayoutContext.Provider value={value}>{children}</LayoutContext.Provider>;
}
